/**
 * Freemium quota policy — pure, client-safe and unit tested.
 *
 * Free accounts may create a small number of items in each tracked area.
 * An active paid subscription lifts every limit. The 24-hour trial is kept
 * for historical/backward compatibility but no longer bypasses these quotas.
 */

export type QuotaFeature = "tests" | "chapters" | "questionChapters";

export const FREE_LIMITS: Record<QuotaFeature, number> = {
  tests: 2,
  chapters: 2,
  questionChapters: 2,
};

export const FEATURE_LABEL: Record<QuotaFeature, string> = {
  tests: "Tests",
  chapters: "Chapters",
  questionChapters: "Question tracker entries",
};

export type QuotaDecision = {
  allowed: boolean;
  reason: "OK" | "LIMIT_REACHED";
  feature: QuotaFeature;
  featureName: string;
  usage: number;
  limit: number | null;
  message: string;
};

/** Decides whether one more item may be created. `unlimited` = active paid plan. */
export function decideQuota(
  feature: QuotaFeature,
  usage: number,
  unlimited: boolean,
): QuotaDecision {
  const featureName = FEATURE_LABEL[feature];
  if (unlimited) {
    return {
      allowed: true,
      reason: "OK",
      feature,
      featureName,
      usage,
      limit: null,
      message: `${featureName}: unlimited on your plan.`,
    };
  }
  const limit = FREE_LIMITS[feature];
  if (usage < limit) {
    return {
      allowed: true,
      reason: "OK",
      feature,
      featureName,
      usage,
      limit,
      message: `${featureName}: ${usage} of ${limit} used on the free plan.`,
    };
  }
  return {
    allowed: false,
    reason: "LIMIT_REACHED",
    feature,
    featureName,
    usage,
    limit,
    message: `You've used all ${limit} ${featureName.toLowerCase()} included in the free plan. Upgrade to Premium for unlimited access.`,
  };
}

const PREFIX = "LIMIT_REACHED:";

/** Encodes a denial into an Error message that survives the RPC boundary. */
export function encodeQuotaError(decision: QuotaDecision): string {
  return `${PREFIX}${JSON.stringify(decision)}`;
}

/** Parses a server error back into a decision, or null when unrelated. */
export function parseQuotaError(error: unknown): QuotaDecision | null {
  const message = error instanceof Error ? error.message : String(error ?? "");
  const at = message.indexOf(PREFIX);
  if (at < 0) return null;
  try {
    return JSON.parse(message.slice(at + PREFIX.length)) as QuotaDecision;
  } catch {
    return null;
  }
}
